/**
 * Column selector component
 * Allows users to show/hide table columns
 */

import { useState } from 'react';
import { Button, Checkbox, Popover, Space, Divider, Typography } from 'antd';
import { SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';

const { Text } = Typography;

export interface ColumnConfig {
  key: string;
  label: string;
  defaultVisible?: boolean;
  locked?: boolean;
}

interface ColumnSelectorProps {
  columns: ColumnConfig[] | Array<{ key: string; label: string; default?: boolean }>;
  visibleColumns?: string[];
  selected?: string[]; // Alias for visibleColumns
  onChange: (columns: string[]) => void;
}

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'category', label: 'Category', defaultVisible: true },
  { key: 'rating', label: 'Rating', defaultVisible: true },
  { key: 'address', label: 'Address', defaultVisible: true },
  { key: 'phone', label: 'Phone', defaultVisible: true },
  { key: 'website', label: 'Website', defaultVisible: true },
  { key: 'coordinates', label: 'Coordinates', defaultVisible: false },
  { key: 'priceLevel', label: 'Price Level', defaultVisible: false },
];

export function ColumnSelector({
  columns = DEFAULT_COLUMNS,
  visibleColumns,
  selected,
  onChange,
}: ColumnSelectorProps) {
  const [open, setOpen] = useState(false);

  // Support both visibleColumns and selected prop names
  const activeColumns = visibleColumns ?? selected ?? [];

  // Normalize columns to have defaultVisible property
  const normalizedColumns = columns.map((col) => ({
    ...col,
    defaultVisible: 'defaultVisible' in col ? col.defaultVisible : (col as any).default,
  }));

  const handleChange = (checkedValues: CheckboxValueType[]) => {
    onChange(checkedValues as string[]);
  };

  const handleReset = () => {
    const defaultVisible = normalizedColumns
      .filter((col) => col.defaultVisible !== false)
      .map((col) => col.key);
    onChange(defaultVisible);
  };

  const handleSelectAll = () => {
    onChange(columns.map((col) => col.key));
  };

  const handleSelectNone = () => {
    onChange([]);
  };

  const content = (
    <div style={{ width: 200 }}>
      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <Button type="link" size="small" onClick={handleSelectAll} style={{ padding: 0 }}>
          All
        </Button>
        <Button type="link" size="small" onClick={handleSelectNone} style={{ padding: 0 }}>
          None
        </Button>
        <Button
          type="link"
          size="small"
          icon={<ReloadOutlined />}
          onClick={handleReset}
          style={{ padding: 0 }}
        >
          Reset
        </Button>
      </Space>

      <Divider style={{ margin: '8px 0' }} />

      <Checkbox.Group
        value={activeColumns}
        onChange={handleChange}
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {normalizedColumns.map((col) => (
          <Checkbox key={col.key} value={col.key} disabled={col.locked}>
            {col.label}
          </Checkbox>
        ))}
      </Checkbox.Group>
    </div>
  );

  return (
    <Popover
      content={content}
      title="Show Columns"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Button icon={<SettingOutlined />}>
        Columns ({activeColumns.length})
      </Button>
    </Popover>
  );
}

/**
 * Hook for managing column visibility
 */
export function useColumnSelector(
  columns: ColumnConfig[] = DEFAULT_COLUMNS,
  storageKey?: string
) {
  const defaultVisible = columns
    .filter((col) => col.defaultVisible !== false)
    .map((col) => col.key);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch {
          return defaultVisible;
        }
      }
    }
    return defaultVisible;
  });

  const handleChange = (columns: string[]) => {
    setVisibleColumns(columns);
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(columns));
    }
  };

  return {
    columns,
    visibleColumns,
    onChange: handleChange,
  };
}

export default ColumnSelector;
